import type { AddEntityRequest } from '@/backend/entity'
import type { APIResult } from '@/backend/types'
import { useLimitedAction } from '@/modules/Billing/useLimitedAction'
import type { DataField, FileCollectionField, FileField } from '@/modules/Project/Fields/types'
import { promiseQueue } from '@/modules/Workspaces/KnowledgeHub/Files/promiseQueue'
import type { IntegrationConnection } from '@/modules/Workspaces/KnowledgeHub/Integrations/integrationTypes'
import { useSetFieldValue } from '@/shared/useSetFieldValue'
import { invariant } from '@/shared/utils/typeAssertions'

type IntegrationFile = { id: string; name: string }

type SharedProps<T extends IntegrationFile> = {
  files: T[]
  getExtraFields?: (file: T) => AddEntityRequest['fields']
  projectId: string
  workspaceId: string
  connection: IntegrationConnection
}

type PropertyProps<T extends IntegrationFile> = SharedProps<T> & {
  propertyId: string
  parentEntityId?: string
}

type FieldProps<T extends IntegrationFile> = SharedProps<T> & {
  field: FileField | FileCollectionField | DataField
}

export async function uploadIntegrationURLs<T extends IntegrationFile>(
  props: PropertyProps<T> | FieldProps<T>,
) {
  const firstFile = props.files.at(0)
  invariant(firstFile)

  // eslint-disable-next-line internal/no-call-composable-outside-setup
  const { addEntity } = useLimitedAction()
  const propertyId = 'propertyId' in props ? props.propertyId : props.field.propertyId
  const parentEntityId = 'parentEntityId' in props ? props.parentEntityId : undefined
  /**
   * List of promises that will create an entity for every given file
   */
  const allFilesCreationPromises = props.files.map((file) => () => {
    return addEntity({
      projectId: props.projectId,
      workspaceId: props.workspaceId,
      parentEntityId,
      fields: {
        ...(props.getExtraFields?.(file) || {}),
        [propertyId]: {
          connection_id: props.connection.id,
          integration_id: props.connection.integrationId,
          file_id: file.id,
          file_name: file.name,
        },
      },
    })
  })

  if ('field' in props) {
    const { propertyId } = props.field

    // eslint-disable-next-line internal/no-call-composable-outside-setup
    const setFieldValue = useSetFieldValue()
    const fields = {
      [propertyId]: {
        field: props.field,
        newValue: {
          connection_id: props.connection.id,
          integration_id: props.connection.integrationId,
          file_id: firstFile.id,
          file_name: firstFile.name,
        },
      },
    }

    await promiseQueue<APIResult<unknown>>(
      [...allFilesCreationPromises, () => setFieldValue(fields)],
      {
        maxConcurrent: 20,
      },
    )
  }

  await promiseQueue(allFilesCreationPromises, { maxConcurrent: 20 })
}
